import React, { useMemo } from 'react';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { Logger } from '@feature-hub/logger';
import { ContentServiceV1 } from '@oneaudi/content-service';
import { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { OneGraphServiceV1 } from '@oneaudi/onegraph-service';

export interface FeatureAppContextState {
  readonly featureAppId: string;
  readonly logger: Logger;
  readonly trackingService: TrackingServiceV2 | undefined;
  readonly contentService: ContentServiceV1;
  readonly renderModeService: RenderModeServiceV1;
  readonly localeService: GfaLocaleServiceV1;
  readonly i18nService: I18NServiceV1;
  readonly oneGraphService: OneGraphServiceV1;
}

export const FeatureAppContext = React.createContext<FeatureAppContextState>(
  {} as FeatureAppContextState,
);
export interface FeatureAppContextProviderProps {
  readonly featureAppId: string;
  readonly logger: Logger;
  readonly trackingService: TrackingServiceV2 | undefined;
  readonly contentService: ContentServiceV1;
  readonly renderModeService: RenderModeServiceV1;
  readonly localeService: GfaLocaleServiceV1;
  readonly i18nService: I18NServiceV1;
  readonly oneGraphService: OneGraphServiceV1;
}

export const FeatureAppContextProvider = ({
  logger,
  children,
  trackingService,
  contentService,
  renderModeService,
  localeService,
  i18nService,
  oneGraphService,
  featureAppId,
}: React.PropsWithChildren<FeatureAppContextProviderProps>): JSX.Element => {
  const contextValue = useMemo(() => {
    return {
      logger,
      trackingService,
      contentService,
      renderModeService,
      localeService,
      i18nService,
      oneGraphService,
      featureAppId,
    };
  }, []);
  return <FeatureAppContext.Provider value={contextValue}>{children}</FeatureAppContext.Provider>;
};

function getFeatureAppContext() {
  const context = React.useContext(FeatureAppContext);
  if (context === null) {
    throw Error('Did you forget to wrap your Feature App in <FeatureAppContextProvider>?');
  }
  return context;
}

export function useLogger(): Logger {
  return getFeatureAppContext().logger;
}
